import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { ENDPOINTS } from 'src/app/_configs/endpoints';
import { PARAMS } from 'src/app/_configs/params';
import { environment } from 'src/environments/environment';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { PaymentService } from './payment.service';

@Injectable({
  providedIn: 'root'
})

export class SharedService {

  isServer: boolean = false;
  isBrowser: boolean = true;
  http: any;

  constructor(private apiService: ApiService,
    private router: Router,
    private paymentService: PaymentService,
    @Inject(PLATFORM_ID) platformId: any) {
    this.isServer = isPlatformServer(platformId);
    this.isBrowser = isPlatformBrowser(platformId);
  }

  retrieveKeywordListTendersNew(): Observable<any> {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_KEYWORD}`;
    return this.apiService.get(url).pipe(
      timeout(3000), // Timeout after 3000 milliseconds
      catchError(error => {
        console.error('Error retrieving keyword list tenders:', error);
        return of([]); // Return empty array or appropriate default value
      })
    );
  }

  retrieveAuthorityListTendersNew(): Observable<any> {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_AUTHORITY}`;
    return this.apiService.get(url).pipe(
      timeout(3000), // Timeout after 3000 milliseconds
      catchError(error => {
        console.error('Error retrieving authority list tenders:', error);
        return of([]); // Return empty array or appropriate default value
      })
    );
  }

  retrieveSeoDetails(type: any, params: any, secondParams: any): Observable<any> {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.RETRIEVE_SEO_DETAILS}?`;

    if (secondParams) {
      url += `state=${params}&${type}=${secondParams}`;
    } else {
      url += `${type}=${params}`;
    }

    return this.apiService.get(url).pipe(
      catchError(error => {
        console.error('Error retrieving SEO details:', error);
        return of(null);
      })
    );
  }

  subscribeTenders(requestData: any): Observable<any> {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.SUBSCRIBE_TENDER}`;
    return this.apiService.post(url, requestData).pipe(
      timeout(3000), // Timeout after 3000 milliseconds
      catchError(error => {
        console.error('Error subscribing to tenders:', error);
        return of(null); // Return null or an appropriate default value
      })
    );
  }

  retrieveFaqDetails(entityId: any, urlSegments: any): Observable<any> {
    let url: string;
    const baseUrl = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.FAQLIST}?entityId=${entityId}`;

    if (Array.isArray(urlSegments) && urlSegments.length > 2) {
      const type = urlSegments[1];

      if (type === 'city' && urlSegments[3]) {
        url = `${baseUrl}&${type}=${urlSegments[3]}&state=${urlSegments[2]}`;
      } else if (type === 'authority' && urlSegments[4]) {
        url = `${baseUrl}&${type}=${urlSegments[2]}&state=${urlSegments[3]}`;
      } else {
        url = `${baseUrl}&${type}=${urlSegments[2]}`;
      }

      // Return the API call with error handling
      return this.apiService.get(url).pipe(
        catchError(error => {
          console.error('Error retrieving FAQ details:', error);
          return of(null); // Return null or a default value on error
        })
      );
    } else {
      url = baseUrl;
      return this.apiService.get(url).pipe(
        catchError(error => {
          console.error('Error retrieving tenders by cities:', error);
          return of({});
        })
      );
    }
  }

  contactUs(contactUs: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.CONTACT_US}`
    return this.apiService.post(url, contactUs);
  }

  unsubscribeAlert(id: string, token: string, source: string) {
    // The alert endpoint sits at the host root, not under the `/api/` path.
    const host = environment.TENDER.API_BASE_URL.replace('/api/', '/');
    const url = `${host}${ENDPOINTS.TENDER.ALERT.UNSUBSCRIBE}?id=${encodeURIComponent(id)}&token=${encodeURIComponent(token)}&source=${encodeURIComponent(source)}`;
    return this.apiService.put(url, '');
  }

  otpSend(mobileNumber: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.OTP.OTP_SEND}?phone=${mobileNumber}`;
    return this.apiService.post(url, mobileNumber);
  }

  otpVerify(requestJson: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.OTP.OPT_VERIFY}`;
    return this.apiService.post(url, requestJson);
  }

  otpSignup(requestJson: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.OTP.OTP_SIGNUP}`;
    return this.apiService.post(url, requestJson);
  }

  appDownloadLink(mobileNumber: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.APP_DOWNLOAD}?phone=${mobileNumber}`;
    return this.apiService.post(url, mobileNumber);
  }

  setPreference(requestJson: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.SET_PREFERENCES}`;
    return this.apiService.post(url, requestJson);
  }

  userinterest(requestJson: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.USER_INTEREST}`;
    return this.apiService.post(url, requestJson);
  }

  // interestPlan(id: any, token: any, plan: any) {
  //   let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.INTEREST_PLAN}`;
  //   return this.apiService.put(url);
  // }

  interestPlan(id: string, token: string, planSelected: string) {
    const baseUrl = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.INTEREST_PLAN}`;
    const url = `${baseUrl}?id=${id}&planSelected=${planSelected}&token=${token}`;
    const payload = {
      id: id,
      token: token,
      plan: planSelected,
    };
    return this.apiService.put(url, payload);
  }

  // downloadFile method to download a file with additional tracking parameters
  downloadFile(fileUrl: string, tenderId?: string, state?: string, authority?: string, userId?: string, downloadAll?: boolean) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.DOWNLOAD_DOC}?url=${encodeURIComponent(fileUrl)}&download=1`;
    if (tenderId) url += `&id=${tenderId}`;
    if (state) url += `&state=${state}`;
    if (authority) url += `&authority=${authority}`;
    if (userId) url += `&userId=${userId}`;
    if (downloadAll) url += `&downloadAll=true`;
    return this.apiService.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      tap((response: any) => {
        // A real file download can consume a plan count on the backend, so drop
        // the cached plan details (any caller, so counts refresh on next read).
        // A JSON response is a business error (e.g. limit exceeded) that consumes
        // nothing, so leave the cache untouched.
        const contentType = response?.headers?.get?.('content-type') || '';
        if (userId && !contentType.includes('application/json')) {
          this.paymentService.clearActivePlanDetailsCache(userId);
        }
      })
    );
  }

  /**
   * The published result documents for one tender, as a zip. The tender's mongoId is
   * the UUID the details route carries as :tenderId.
   */
  downloadResultDocuments(mongoId: string, userId: string) {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.DOWNLOAD_RESULT}`
      + `?mongoId=${encodeURIComponent(mongoId)}&userId=${encodeURIComponent(userId)}`;
    return this.apiService.get(url, { responseType: 'blob', observe: 'response' });
  }

  /**
   * A tender's corrigendum documents, gated like its tender documents: one document
   * (key + index), one corrigendum's (key), or all of them. Several come as a zip.
   */
  downloadCorrigendumDocuments(mongoId: string, userId: string, key?: string | null, index?: number | null,
                               inline = false) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.DOWNLOAD_CORRIGENDUM}`
      + `?mongoId=${encodeURIComponent(mongoId)}&userId=${encodeURIComponent(userId)}`;
    if (key) {
      url += `&key=${encodeURIComponent(key)}`;
      if (index !== null && index !== undefined) {
        url += `&index=${index}`;
      }
    }
    if (inline) {
      url += '&inline=true';
    }
    return this.apiService.get(url, { responseType: 'blob', observe: 'response' });
  }

  /**
   * Asks the portal for this tender's latest documents. The scrape takes minutes, so this only starts the run -
   * follow it with getTenderDocumentRefreshStatus.
   */
  startTenderDocumentRefresh(mongoId: string, userId: string) {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.REFRESH_DOCUMENTS}`
      + `?userId=${encodeURIComponent(userId)}&mongoId=${encodeURIComponent(mongoId)}`;
    return this.apiService.post(url, null);
  }

  getTenderDocumentRefreshStatus(mongoId: string, userId: string) {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.REFRESH_DOCUMENTS_STATUS}`
      + `?userId=${encodeURIComponent(userId)}&mongoId=${encodeURIComponent(mongoId)}`;
    return this.apiService.get(url);
  }

  verifySearchBarShowHideCond() {
    const path = this.router.url.split('?')[0];
    if (path !== PARAMS.ROUTING_PATHS.HOME && path !== PARAMS.ROUTING_PATHS.ABOUT_US && path !== PARAMS.ROUTING_PATHS.LANDING_PAGE && path !== PARAMS.ROUTING_PATHS.PROFILE) {
      return true
    }
    else {
      return false
    }
  }

  retrievetrending(type: string, value: string, authorityState?: string): Observable<any> {

    const baseUrl = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.RETRIEVE_TRENDING}`;
    let url = `${baseUrl}?${type}=${value}`;

    if (type === 'authority' && authorityState) {
      url += `&state=${authorityState}`;
    }

    return this.apiService.get(url).pipe(
      catchError(error => {
        console.error('Error retrieving trending data:', error);
        return of(null);
      })
    );
  }
  retrievetrendingSearches(type: any, value: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.TRENDING_SEARCH}?${type}=${value}`
    return this.apiService.get(url)
  }


  tenderDetailsPageUrl(tender: any): string {
    return `/details/${tender?.state?.link}-tender/${tender?.authority?.name}/${tender?.id}`;
  }

  getCurrentPageUrl() {
    return this.router['location']._platformLocation.location;
  }

  getBlogList(pageSize: any, pageNumber: any, slug: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.BLOG_LIST}?pageNumber=${pageNumber}&postSize=${pageSize}&slug=${slug}`;
    return this.apiService.get(url);
  }

  getNewlyTenders() {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.NEWLY_ADDED_TENDERS}`;
    return this.apiService.get(url);
  }

  retrieveStateListTenders() {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_STATE}`;
    return this.apiService.get(url);
  }

  getBlogDetails(slug: any) {
    const url = `${environment.TENDER.BLOG_BASE_URL}${ENDPOINTS.TENDER.BLOG.BLOG_Details}?_embed&slug=${slug}`;
    return this.apiService.get(url).pipe(
      map((response: any) => {
        const blog = response[0];
        if (!blog) {
          throw new Error('Blog not found');
        }
        // Check if `_embedded` and `wp:featuredmedia` exist and have at least one item
        const featureImage = blog._embedded && blog._embedded['wp:featuredmedia']
          ? blog._embedded['wp:featuredmedia'][0]?.source_url || '' : '';

        const authorData = blog._embedded && blog._embedded.author ? blog._embedded.author[0] : null;
        let author = null;
        if (authorData) {
          const getYoastGraph = (details: any) => {
            if (details.yoast_head_json?.schema?.["@graph"]) return details.yoast_head_json.schema["@graph"];
            if (details.yoast_head_json?.schema?.graph) return details.yoast_head_json.schema.graph;
            if (typeof details.yoast_head === 'string') {
              const match = details.yoast_head.match(/<script type="application\/ld\+json" class="yoast-schema-graph">(.*?)<\/script>/);
              if (match && match[1]) {
                try {
                  return JSON.parse(match[1])["@graph"] || [];
                } catch (e) {
                  console.error("Error parsing Yoast graph:", e);
                }
              }
            }
            return [];
          };

          const graph = getYoastGraph(authorData);
          const person = graph.find((item: any) => item["@type"] === "Person" || (Array.isArray(item["@type"]) && item["@type"].includes("Person"))) || graph[3] || {};
          const sameAs = person.sameAs || [];
          const linkedInUrl = blog.author_socials?.linkedin || sameAs.find((url: any) => typeof url === 'string' && url.includes("linkedin.com")) || "";
          const authorProfileImage = blog.author_socials?.custom_avatar || ((typeof sameAs[1] === 'string' && sameAs[1].startsWith('http')) ? sameAs[1] : (authorData.avatar_urls?.['96'] || ""));
          const authorDesignation = blog.author_socials?.job_title || authorData.author_socials?.job_title || (typeof sameAs[2] === 'string' ? sameAs[2] : "");

          author = {
            id: authorData.id,
            name: authorData.name,
            slug: authorData.slug,
            description: authorData.description || '',
            linkedInUrl,
            profileImage: authorProfileImage,
            designation: authorDesignation
          };
        }

        return {
          id: blog.id,
          title: blog.title?.rendered || 'No Title',
          content: blog.content?.rendered || 'No Content',
          date: new Date(blog.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          modified: new Date(blog.modified || blog.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          slug: blog.slug || '',
          featureImage: featureImage,
          author: author
        };
      })
    );
  }

  getAuthorDetails(slug: string): Observable<any> {
    const url = `${environment.TENDER.BLOG_BASE_URL}${ENDPOINTS.TENDER.BLOG.AUTHOR_Details}?search=${slug}`;
    return this.apiService.get(url).pipe(
      map((response: any[]) => {
        const author = response.find((item: any) => item.slug === slug);
        if (!author || author.slug === 'admin') {
          throw new Error('Author not found');
        }
        return author;
      })
    );
  }

  getAuthorPosts(authorId: number, page: number, perPage: number = 12): Observable<any> {
    const url = `${environment.TENDER.BLOG_BASE_URL}${ENDPOINTS.TENDER.BLOG.BLOG_Details}?author=${authorId}&_embed&page=${page}&per_page=${perPage}`;
    return this.apiService.get(url, { observe: 'response' }).pipe(
      map((response: any) => {
        return {
          posts: response.body,
          totalPages: parseInt(response.headers.get('x-wp-totalpages')) || 1,
          totalPosts: parseInt(response.headers.get('x-wp-total')) || 0
        };
      })
    );
  }

  guestIdentify(guestId: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.GUESTIDENTIFY}?guestId=${guestId}`;
    return this.apiService.post(url, guestId);
  }

  // checkUserSubscription method to fetch user subscription status
  checkUserSubscription(userId: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.USER_SUBSCRIPTION_CHECK}?userId=${userId}`;
    return this.apiService.get(url);
  }

  planInterest(userid: any, selectedPlan: any) {
    let url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.PLANINTEREST}?planSelected=${selectedPlan}&userId=${userid}`;
    return this.apiService.post(url, userid);
  }

  listingExport(payload: any) {
    const url = `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CSV_EXPORT}`;
    return this.apiService.post(url, payload, { responseType: 'blob' });
  }

  retrieveValidDate(date: any): string {
    const parseDate = (date: any): Date | null => {
      if (typeof date === 'string') {
        const parsedDate = new Date(date);
        return !isNaN(parsedDate.getTime()) ? parsedDate : null;
      } else if (date instanceof Date) {
        return !isNaN(date.getTime()) ? date : null;
      } else if (typeof date === 'number') {
        const parsedDate = new Date(date);
        return !isNaN(parsedDate.getTime()) ? parsedDate : null;
      }
      return null;
    };

    const parsedDate = parseDate(date);
    return parsedDate ? parsedDate.toISOString().split('T')[0] : '';
  }

  // retrieveValidDate(date: any): string {
  //   const parseDate = (date: any): Date | null => {
  //     if (typeof date === 'string') {
  //       const parsedDate = parseISO(date);
  //       return isValid(parsedDate) ? parsedDate : null;
  //     } else if (date instanceof Date) {
  //       return date;
  //     } else if (typeof date === 'number') {
  //       const parsedDate = new Date(date);
  //       return isValid(parsedDate) ? parsedDate : null;
  //     }
  //     return null;
  //   };

  //   const parsedDate = parseDate(date);
  //   return parsedDate ? format(parsedDate, 'yyyy-MM-dd') : '';
  // }

  capitalizeTheFirstLetterOfEachWord(type: string) {
    var typeWithoutUnderscore = type.replace(PARAMS.REGEX.UNDERSOCRE_REPLACE_WITH_SPACE_REGEX, " ");
    var separateWord = typeWithoutUnderscore.toLowerCase().split(' ');

    for (var i = 0; i < separateWord?.length; i++) {
      separateWord[i] = separateWord[i].charAt(0).toUpperCase() +
        separateWord[i].substring(1);
    }

    return separateWord.join(' ');
  }

  homepageNavigationFixedOnMobileView() {
    if (this.isBrowser) {
      let elements = document.getElementById("main_header") as HTMLElement;

      function fixedviewOnScroll(_verticalScrollP: number, _className: string, _elementnName: Element) {
        if (window?.scrollY > _verticalScrollP) {
          _elementnName?.classList?.add(_className);
        }
        else {
          _elementnName?.classList?.remove(_className);
        }
      }

      if (window?.outerWidth < 576) {
        (function fixedheaderonscroll() {
          fixedviewOnScroll(30, "fixed", elements)
        })();
      }
    }
  }

  tenderDetailsPageNavigationFixedOnMobileView(numberOfDocs: number) {
    if (this.isBrowser) {
      let elements = document.getElementById("navTabshorizontal") as HTMLElement;

      function FixedviewOnScroll(_verticalScrollP: number, _className: string, _elementnName: Element) {
        if (window?.scrollY > _verticalScrollP) {
          _elementnName?.classList?.add(_className);
        }
        else {
          _elementnName?.classList?.remove(_className);
        }
      }

      if (numberOfDocs > 0) {
        FixedviewOnScroll(300, "navtabs-fixed", elements)
      }
    }
  }

  onScroll(event: Event) {
    let data = { isRightVisible: true, isLeftVisible: true };
    let val = (event.target as HTMLElement).scrollLeft;
    let maxScrollLeft = (event.target as HTMLElement).scrollWidth - (event.target as HTMLElement).clientWidth;
    if (val == 0) data.isLeftVisible = false
    else {
      data.isLeftVisible = true;
    }
    if (val == maxScrollLeft) data.isRightVisible = false;
    else data.isRightVisible = true;
    return data;
  }
  scrollDiv(containerId: string, numberOfItems: number, right: boolean) {
    const element: any = document.getElementById(containerId);
    // `firstElementChild` skips the comment anchors Angular leaves behind for
    // structural directives; a comment node has no offsetWidth, which would
    // make the scroll step NaN.
    const firstCard: any = element?.firstElementChild;
    if (!firstCard) {
      return;
    }
    const gap = parseFloat(getComputedStyle(element).columnGap) || 0;
    const scrollAmount = firstCard.offsetWidth + gap;
    right ? element.scrollLeft += scrollAmount : element.scrollLeft -= scrollAmount
  }
}

export interface ServerLogSchema {
  logId?: any,
  startDateTime: any;
  endDateTime: any;
  startDateTimeV2?: any;
  endDateTimev2?: any;
  apiRequestTime?: any,
  apiURL: string;
  method: string;
  payload: any;
  responseStatus: any;
  sessionId: any;
}